import React, { useState, useRef } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, User, Phone, AlertTriangle, Target, CreditCard, Camera, DollarSign } from 'lucide-react';
import ClientPaymentInfo from '../accounting/ClientPaymentInfo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Profile Avatar Component with upload capability
const ProfileAvatar = ({ imageUrl, name, userId, onImageUpdate, canEdit = false }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecciona un archivo de imagen');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen debe ser menor de 5MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/users/${userId}/profile-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (onImageUpdate) onImageUpdate();
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al subir imagen: ' + (error.response?.data?.detail || 'Error desconocido'));
    } finally {
      setUploading(false);
    }
  };

  const initials = name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';

  return (
    <div className="relative">
      <div 
        className={`w-16 h-16 rounded-full overflow-hidden flex items-center justify-center ${
          imageUrl ? '' : 'bg-blue-500/30'
        } ${canEdit ? 'cursor-pointer' : ''}`}
        onClick={() => canEdit && fileInputRef.current?.click()}
        title={canEdit ? 'Clic para cambiar foto' : ''}
      >
        {imageUrl ? (
          <img 
            src={`${BACKEND_URL}${imageUrl}`} 
            alt={name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className={`w-full h-full flex items-center justify-center ${imageUrl ? 'hidden' : ''}`}
          style={{ display: imageUrl ? 'none' : 'flex' }}
        >
          {uploading ? (
            <div className="animate-spin w-6 h-6 border-2 border-blue-200 border-t-transparent rounded-full" />
          ) : (
            <span className="text-blue-200 text-xl font-bold">{initials}</span>
          )}
        </div>
      </div>
      
      {canEdit && (
        <>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute bottom-0 right-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-400 transition-colors"
            title="Cambiar foto"
            type="button"
            disabled={uploading}
          >
            <Camera className="w-3 h-3 text-white" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

// Collapsible Section Component (Spanish labels)
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true, alertCount = 0 }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-blue-900/20 rounded-lg overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-blue-800/30 transition-colors"
        type="button"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-400" />}
          <span className="text-white font-medium">{title}</span>
          {alertCount > 0 && (
            <span className="bg-red-500/30 text-red-200 text-xs px-2 py-0.5 rounded-full">
              {alertCount}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-blue-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-blue-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 border-t border-blue-400/20">
          {children}
        </div>
      )}
    </div>
  );
};

// Field Display Component (for view mode)
const FieldDisplay = ({ label, value, className = '' }) => (
  <div className={className}>
    <label className="block text-blue-300 text-xs mb-1">{label}</label>
    <p className="text-white text-sm">{value || 'No indicado'}</p>
  </div>
);

// Field Input Component (for edit mode)
const FieldInput = ({ label, type = 'text', value, onChange, placeholder, disabled, className = '', ...props }) => (
  <div className={className}>
    <label className="block text-blue-200 text-sm font-medium mb-1">{label}</label>
    {type === 'textarea' ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={`input-field text-sm w-full ${disabled ? 'bg-blue-900/30 cursor-not-allowed' : ''}`}
        placeholder={placeholder}
        disabled={disabled}
        {...props}
      />
    ) : (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(type === 'number' ? (e.target.value ? parseFloat(e.target.value) : '') : e.target.value)}
        className={`input-field text-sm w-full ${disabled ? 'bg-blue-900/30 cursor-not-allowed' : ''}`}
        placeholder={placeholder}
        disabled={disabled}
        {...props}
      />
    )}
  </div>
);

const ClientProfile = ({ profile, onUpdateProfile, t }) => {
  const [isEditing, setIsEditing] = useState(false);
  
  const initialFormData = {
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    height: profile?.height || '',
    weight: profile?.weight || '',
    training_objectives: profile?.training_objectives || '',
    injury_history_note: profile?.injury_history_note || '',
    emergency_contact: profile?.emergency_contact || '',
    emergency_contact_name: profile?.emergency_contact_name || '',
    emergency_contact_relationship: profile?.emergency_contact_relationship || '',
    emergency_contact_phone: profile?.emergency_contact_phone || '',
    address: profile?.address || '',
    postal_code: profile?.postal_code || '',
    city: profile?.city || ''
  };
  
  const [formData, setFormData] = useState(initialFormData);
  
  const resetFormData = () => {
    setFormData({
      full_name: profile?.full_name || '',
      phone: profile?.phone || '',
      height: profile?.height || '',
      weight: profile?.weight || '',
      training_objectives: profile?.training_objectives || '',
      injury_history_note: profile?.injury_history_note || '',
      emergency_contact: profile?.emergency_contact || '',
      emergency_contact_name: profile?.emergency_contact_name || '',
      emergency_contact_relationship: profile?.emergency_contact_relationship || '',
      emergency_contact_phone: profile?.emergency_contact_phone || '',
      address: profile?.address || '',
      postal_code: profile?.postal_code || '',
      city: profile?.city || ''
    });
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        full_name: formData.full_name,
        phone: formData.phone,
        height: formData.height ? parseFloat(formData.height) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        training_objectives: formData.training_objectives,
        injury_history_note: formData.injury_history_note,
        emergency_contact: formData.emergency_contact,
        emergency_contact_name: formData.emergency_contact_name,
        emergency_contact_relationship: formData.emergency_contact_relationship,
        emergency_contact_phone: formData.emergency_contact_phone,
        address: formData.address,
        postal_code: formData.postal_code,
        city: formData.city
      };

      await axios.put(`${API}/profile`, updateData);
      alert('✅ ¡Perfil actualizado con éxito!');
      setIsEditing(false);
      onUpdateProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('❌ Error al actualizar el perfil: ' + (error.response?.data?.detail || 'Error desconocido'));
    }
  };

  if (!profile) {
    return <div className="text-white">Cargando perfil...</div>;
  }

  const handleEditClick = () => {
    if (!isEditing) {
      resetFormData();
    }
    setIsEditing(!isEditing);
  };

  // Calculate age from birth_date if available
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = profile.age || calculateAge(profile.birth_date);
  const hasInjury = profile.injury_history_note && profile.injury_history_note.trim().length > 0;

  // Format status badge
  const getStatusBadge = () => {
    if (profile.client_status === 'onboarding') {
      return <span className="bg-yellow-500/20 text-yellow-200 text-xs px-2 py-0.5 rounded">En Adaptación</span>;
    } else if (profile.client_status === 'active' || profile.is_active !== false) {
      return <span className="bg-green-500/20 text-green-200 text-xs px-2 py-0.5 rounded">Activo</span>;
    }
    return <span className="bg-red-500/20 text-red-200 text-xs px-2 py-0.5 rounded">Inactivo</span>;
  };

  const getBookingModeBadge = () => {
    if (profile.booking_mode === 'monthly_fixed') {
      return <span className="bg-purple-500/20 text-purple-200 text-xs px-2 py-0.5 rounded">Mensual Fijo</span>;
    } else if (profile.booking_mode === 'booking_flex') {
      return <span className="bg-blue-500/20 text-blue-200 text-xs px-2 py-0.5 rounded">Reserva Flexible</span>;
    }
    return null;
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Mi Perfil</h2>
        <button
          onClick={handleEditClick}
          className={isEditing ? 'btn-secondary' : 'btn-primary'}
        >
          {isEditing ? 'Cancelar' : '✏️ Editar perfil'}
        </button>
      </div>

      {/* Profile Header Card */}
      <div className="glass-card p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <ProfileAvatar 
              imageUrl={profile.profile_image_url}
              name={profile.full_name}
              userId={profile.id}
              onImageUpdate={onUpdateProfile}
              canEdit={true}
            />
            <div>
              <h1 className="text-2xl font-bold text-white">{profile.full_name}</h1>
              <p className="text-blue-300">{profile.email}</p>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {getStatusBadge()}
                {getBookingModeBadge()}
                {profile.member_number && (
                  <span className="text-blue-400 text-xs">Socio #{profile.member_number}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit}>
          {/* Personal Information - Edit Mode */}
          <CollapsibleSection title="Información Personal" icon={User} defaultOpen={true}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
              <FieldInput
                label="Nombre completo"
                value={formData.full_name}
                onChange={(v) => handleFieldChange('full_name', v)}
                required
              />
              <FieldInput
                label="Email"
                type="email"
                value={profile.email}
                disabled
                className="opacity-60"
              />
              <FieldInput
                label="Teléfono"
                type="tel"
                value={formData.phone}
                onChange={(v) => handleFieldChange('phone', v)}
              />
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-1">Edad</label>
                <p className="text-white text-sm py-2">{age ? `${age} años` : 'No indicado'}</p>
                <p className="text-blue-400 text-xs">Se calcula automáticamente</p>
              </div>
            </div>
          </CollapsibleSection>

          {/* Contact Information - Edit Mode */}
          <CollapsibleSection title="Dirección" icon={Phone} defaultOpen={true}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
              <FieldInput
                label="Dirección"
                value={formData.address}
                onChange={(v) => handleFieldChange('address', v)}
                placeholder="Calle, número..."
                className="md:col-span-2"
              />
              <FieldInput
                label="Código Postal"
                value={formData.postal_code}
                onChange={(v) => handleFieldChange('postal_code', v)}
              />
              <FieldInput
                label="Población"
                value={formData.city}
                onChange={(v) => handleFieldChange('city', v)}
              />
            </div>
          </CollapsibleSection>

          {/* Emergency Contact - Edit Mode */}
          <CollapsibleSection title="Contacto de Emergencia" icon={AlertTriangle} defaultOpen={false}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
              <FieldInput
                label="Nombre del contacto"
                value={formData.emergency_contact_name}
                onChange={(v) => handleFieldChange('emergency_contact_name', v)}
                placeholder="Nombre completo"
              />
              <FieldInput
                label="Parentesco"
                value={formData.emergency_contact_relationship}
                onChange={(v) => handleFieldChange('emergency_contact_relationship', v)}
                placeholder="ej. Padre, Pareja"
              />
              <FieldInput
                label="Teléfono"
                type="tel"
                value={formData.emergency_contact_phone}
                onChange={(v) => handleFieldChange('emergency_contact_phone', v)}
              />
              <FieldInput
                label="Info adicional (legado)"
                value={formData.emergency_contact}
                onChange={(v) => handleFieldChange('emergency_contact', v)}
                className="md:col-span-3"
              />
            </div>
          </CollapsibleSection>

          {/* Physical Stats - Edit Mode */}
          <CollapsibleSection title="Datos Físicos" icon={Target} defaultOpen={false}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
              <FieldInput
                label="Altura (cm)"
                type="number"
                value={formData.height}
                onChange={(v) => handleFieldChange('height', v)}
                step="0.1"
                min="100"
                max="250"
              />
              <FieldInput
                label="Peso (kg)"
                type="number"
                value={formData.weight}
                onChange={(v) => handleFieldChange('weight', v)}
                step="0.1"
                min="20"
                max="300"
              />
            </div>
          </CollapsibleSection>

          {/* Training & Medical - Edit Mode */}
          <CollapsibleSection title="Entrenamiento y Salud" icon={Target} defaultOpen={true} alertCount={hasInjury ? 1 : 0}>
            <div className="space-y-4 pt-4">
              <FieldInput
                label="Objetivos de entrenamiento"
                type="textarea"
                value={formData.training_objectives}
                onChange={(v) => handleFieldChange('training_objectives', v)}
                placeholder="¿Cuáles son tus objetivos de fitness?"
                rows={3}
              />
              <FieldInput
                label="Lesiones / Patologías"
                type="textarea"
                value={formData.injury_history_note}
                onChange={(v) => handleFieldChange('injury_history_note', v)}
                placeholder="Describe cualquier lesión o patología relevante..."
                rows={3}
              />
            </div>
          </CollapsibleSection>

          {/* ERP: Commercial Info - Read Only */}
          <CollapsibleSection title="Mi Plan" icon={CreditCard} defaultOpen={false}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
              <FieldDisplay 
                label="Cuota Mensual" 
                value={profile.monthly_fee ? `${profile.monthly_fee} ${profile.currency || 'EUR'}` : 'No definida'} 
              />
              <FieldDisplay 
                label="Modalidad" 
                value={
                  profile.booking_mode === 'monthly_fixed' ? 'Mensual Fijo' :
                  profile.booking_mode === 'booking_flex' ? 'Reserva Flexible' :
                  'No definida'
                } 
              />
              <FieldDisplay label="Moneda" value={profile.currency || 'EUR'} />
            </div>
            <p className="text-blue-400 text-xs mt-3">
              Para cambios en tu plan, contacta con tu entrenador.
            </p>
          </CollapsibleSection>

          <button type="submit" className="btn-primary w-full mt-4">
            💾 Guardar cambios
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          {/* Personal Information - View Mode */}
          <CollapsibleSection title="Información Personal" icon={User} defaultOpen={true}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
              <FieldDisplay label="Nombre" value={profile.full_name} />
              <FieldDisplay label="Email" value={profile.email} />
              <FieldDisplay label="Teléfono" value={profile.phone} />
              <FieldDisplay label="Edad" value={age ? `${age} años` : null} />
              <FieldDisplay label="Género" value={profile.gender?.replace('_', ' ')} />
              <FieldDisplay label="Nacionalidad" value={profile.nationality} />
            </div>
          </CollapsibleSection>

          {/* Contact Information - View Mode */}
          <CollapsibleSection title="Dirección" icon={Phone} defaultOpen={true}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
              <FieldDisplay label="Dirección" value={profile.address} className="md:col-span-2" />
              <FieldDisplay label="Código Postal" value={profile.postal_code} />
              <FieldDisplay label="Población" value={profile.city} />
            </div>
          </CollapsibleSection>

          {/* Emergency Contact - View Mode */}
          <CollapsibleSection title="Contacto de Emergencia" icon={AlertTriangle} defaultOpen={false}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
              <FieldDisplay label="Nombre del contacto" value={profile.emergency_contact_name} />
              <FieldDisplay label="Parentesco" value={profile.emergency_contact_relationship} />
              <FieldDisplay label="Teléfono" value={profile.emergency_contact_phone} />
              {profile.emergency_contact && (
                <FieldDisplay label="Info adicional" value={profile.emergency_contact} className="md:col-span-3" />
              )}
            </div>
          </CollapsibleSection>

          {/* Physical Stats - View Mode */}
          <CollapsibleSection title="Datos Físicos" icon={Target} defaultOpen={false}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
              <FieldDisplay label="Altura" value={profile.height ? `${profile.height} cm` : null} />
              <FieldDisplay label="Peso" value={profile.weight ? `${profile.weight} kg` : null} />
              {profile.height && profile.weight && (
                <FieldDisplay 
                  label="IMC" 
                  value={(profile.weight / Math.pow(profile.height / 100, 2)).toFixed(1)} 
                />
              )}
            </div>
          </CollapsibleSection>

          {/* Training & Medical - View Mode */}
          <CollapsibleSection title="Entrenamiento y Salud" icon={Target} defaultOpen={true} alertCount={hasInjury ? 1 : 0}>
            <div className="space-y-4 pt-4">
              <div>
                <label className="block text-blue-300 text-xs mb-1">Objetivos de entrenamiento</label>
                <p className="text-white text-sm whitespace-pre-wrap bg-blue-900/30 p-3 rounded-lg">
                  {profile.training_objectives || 'No se han definido objetivos'}
                </p>
              </div>
              {profile.injury_history_note && (
                <div className="bg-red-900/20 border border-red-400/30 p-3 rounded-lg">
                  <label className="block text-red-300 text-xs mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Lesiones / Patologías
                  </label>
                  <p className="text-red-100 text-sm whitespace-pre-wrap">
                    {profile.injury_history_note}
                  </p>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* ERP: Commercial Info - Read Only */}
          <CollapsibleSection title="Mi Plan" icon={CreditCard} defaultOpen={false}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
              <FieldDisplay 
                label="Cuota Mensual" 
                value={profile.monthly_fee ? `${profile.monthly_fee} ${profile.currency || 'EUR'}` : 'No definida'} 
              />
              <FieldDisplay 
                label="Modalidad" 
                value={
                  profile.booking_mode === 'monthly_fixed' ? 'Mensual Fijo' :
                  profile.booking_mode === 'booking_flex' ? 'Reserva Flexible' :
                  'No definida'
                } 
              />
              <FieldDisplay label="Forma de pago" value={profile.payment_preference || 'No definida'} />
            </div>
            <p className="text-blue-400 text-xs mt-3">
              Para cambios en tu plan, contacta con tu entrenador.
            </p>
          </CollapsibleSection>

          {/* Legal Info - Read Only */}
          <CollapsibleSection title="Información Legal" icon={CreditCard} defaultOpen={false}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
              <div className="bg-blue-900/30 p-3 rounded-lg">
                <div className="text-blue-300 text-xs mb-1">Términos aceptados</div>
                <div className="text-white font-medium text-sm">
                  {profile.terms_accepted ? (
                    <span className="text-green-300">Sí</span>
                  ) : (
                    <span className="text-red-300">No</span>
                  )}
                </div>
              </div>
              <div className="bg-blue-900/30 p-3 rounded-lg">
                <div className="text-blue-300 text-xs mb-1">Fecha de aceptación</div>
                <div className="text-white font-medium text-sm">
                  {profile.terms_accepted_at 
                    ? new Date(profile.terms_accepted_at).toLocaleDateString('es-ES')
                    : '-'}
                </div>
              </div>
              <div className="bg-blue-900/30 p-3 rounded-lg">
                <div className="text-blue-300 text-xs mb-1">Política de privacidad</div>
                <div className="text-white font-medium text-sm">
                  {profile.privacy_policy_accepted ? (
                    <span className="text-green-300">Aceptada</span>
                  ) : (
                    <span className="text-red-300">No aceptada</span>
                  )}
                </div>
              </div>
              <div className="bg-blue-900/30 p-3 rounded-lg">
                <div className="text-blue-300 text-xs mb-1">Fecha aceptación privacidad</div>
                <div className="text-white font-medium text-sm">
                  {profile.privacy_policy_accepted_at 
                    ? new Date(profile.privacy_policy_accepted_at).toLocaleDateString('es-ES')
                    : '-'}
                </div>
              </div>
              <div className="bg-blue-900/30 p-3 rounded-lg">
                <div className="text-blue-300 text-xs mb-1">Versión legal</div>
                <div className="text-white font-medium text-sm">
                  {profile.legal_version || '-'}
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Payment Info - Read Only */}
          <CollapsibleSection title="Estado de Pago" icon={DollarSign} defaultOpen={false}>
            <div className="pt-4">
              <ClientPaymentInfo clientId={profile.id} />
            </div>
          </CollapsibleSection>
        </div>
      )}
    </div>
  );
};

export default ClientProfile;
